import { useState } from "react";
import { cn } from "@/lib/utils";
import Link from "next/link";

type FaqItem = {
  q: string;
  a: string;
};

export const LANDING_FAQS: FaqItem[] = [
  {
    q: "Where does my app run?",
    a: "On a dedicated Linux server - a machine in a data center that runs 24/7 and serves your app to everyone who visits your URL. With DollarDeploy it's yours alone: no shared resources and no usage-based surprises."
  },
  {
    q: "Do I need to know Docker / Kubernetes / AWS?",
    a: "No. You tell us what you built, and we handle provisioning, builds, HTTPS and deploys in the background. You can go as deep as you want, but you never have to."
  },
  {
    q: "What if my app breaks?",
    a: 'Ask the AI agent - "it\'s returning 500s" - and it checks logs, suggests a fix, and can redeploy. It takes real actions on your server, within safe guardrails. A human team is on Discord too.'
  },
  {
    q: "How is this different from Vercel or Netlify?",
    a: "Serverless is great until a viral moment turns into a $600 bill. We run your app on a fixed-price server you own - predictable cost, no surprise scaling charges, and no lock-in."
  },
  {
    q: "What does it cost?",
    a: "There's a free plan with 1 app and 1 server. Paid plans start at $4/mo for DollarDeploy plus your server (from $7/mo on Hetzner or DigitalOcean) - 14-day free trial, cancel anytime, and money-back if it's not for you."
  },
  {
    q: "What if my code is just on my laptop?",
    a: "We'll walk you through pushing it to GitHub - about three minutes. From there, every git push deploys automatically."
  }
];

const EXTENDED_FAQS: FaqItem[] = [
  {
    q: "I built my app with AI (Cursor, Claude, etc.). Can DollarDeploy get it live?",
    a: "That's exactly what we're built for. Connect your GitHub repo, and DollarDeploy auto-detects your stack, builds your app, configures HTTPS, and deploys it to a server - all without you touching a terminal. Our AI agent can also set things up for you if you describe what you need in plain language."
  },
  {
    q: "What does the AI agent do?",
    a: "It takes real actions on your infrastructure - not just gives advice. You can tell it to deploy an app, provision a new server, install a database, or troubleshoot a production error, all in plain language. Think of it as a DevOps engineer that works instantly and never sleeps."
  },
  {
    q: "What apps and frameworks can I deploy?",
    a: "Next.js, React, Node.js, Python, Go, Rust, Java, PHP - and anything else via Docker Compose. Your apps run natively as system services (no Docker overhead), so they're fast and lightweight. We auto-detect your framework and configure the build automatically."
  },
  {
    q: "Will my app be good for SEO and AI agents?",
    a: "Yes. Unlike serverless platforms with cold starts and edge-function quirks, your app runs as a persistent service on a real server. That means consistent response times, proper server-side rendering, no cold-start delays for crawlers, and full control over headers, robots.txt, and sitemaps."
  },
  {
    q: "Which cloud providers can I use?",
    a: "Any Linux server with SSH access works. We have one-click provisioning for DigitalOcean, Hetzner, Verda Cloud (GPU servers), and AWS. You can also bring your own VPS, bare-metal server, or even a Raspberry Pi."
  },
  {
    q: "Can I switch cloud providers later?",
    a: "Yes, and that's the point. Since DollarDeploy uses standard primitives - a Linux VPS, Postgres, object storage - there's no proprietary runtime locking you in. Provision a new server on a different provider, redeploy your apps, and you're done."
  },
  {
    q: "How does native execution work (no Docker)?",
    a: "DollarDeploy builds your app and deploys the result directly to your server as a systemd service - the same way production software runs on Linux. No Docker containers, no registry overhead, no YAML files. Better performance on cheaper hardware."
  },
  {
    q: "How are HTTPS certificates handled?",
    a: "Automatically. Point your domain to your server, add it in the dashboard, and we obtain and renew free Let's Encrypt SSL certificates with zero manual steps. Certificates renew before they expire, so you never think about HTTPS again."
  },
  {
    q: "How do backups work?",
    a: "One click or on a schedule. Back up your databases and application files directly from the dashboard to S3-compatible storage. You can configure backups per app or per server, and restore when needed. No scripts to write, no cron jobs to maintain."
  },
  {
    q: "Can I deploy AI models or LLMs?",
    a: "Yes. Provision GPU-enabled servers from Verda Cloud directly from the dashboard, and deploy LLMs like DeepSeek or open-source models in one click. DollarDeploy handles the server setup so you can focus on the model."
  },
  {
    q: "Is my data secure? Who has access to my server?",
    a: "Your code, data, and server remain under your control. DollarDeploy only uses SSH access to manage deployments and configurations - we don't store your application data. SSH keys, API tokens, and credentials are encrypted at rest. You can revoke access at any time."
  },
  {
    q: "How is this different from Coolify or Dokploy?",
    a: "Coolify and Dokploy are open-source self-hosted PaaS tools - they're powerful, but they require Docker knowledge, terminal comfort, and you have to host and maintain the platform itself. DollarDeploy runs apps natively (no Docker needed), includes a built-in AI agent, and is a managed service so there's nothing to self-host."
  }
];

export const ALL_FAQS: FaqItem[] = [...LANDING_FAQS, ...EXTENDED_FAQS];

export const FAQ2 = ({ landing }: { landing?: boolean }) => {
  const faqs = landing ? LANDING_FAQS : ALL_FAQS;
  const [open, setOpen] = useState<number>(0);

  const handleToggle = (i: number) => {
    setOpen(open === i ? -1 : i);
  };

  return (
    <section id="faq" className="max-w-[1140px] mx-auto px-8 py-24">
      <div className="max-w-[720px] mx-auto text-center mb-14">
        <div className="inline-block text-xs text-primary bg-primary/8 px-3 py-1 rounded-full mb-4 font-medium tracking-wide lowercase font-mono">
          frequently asked
        </div>
        <h2 className="font-serif text-[clamp(36px,5.5vw,56px)] leading-[1.05] tracking-tight font-normal mb-4 text-foreground text-balance">
          Questions
          {landing ? (
            <>
              &nbsp;you <em className="text-primary">might</em> have.
            </>
          ) : null}
        </h2>
      </div>
      <div
        className="max-w-[760px] mx-auto bg-card border rounded-xl overflow-hidden shadow-sm"
        role="list"
      >
        {faqs.map((f, i) => (
          <div
            key={i}
            className={cn(
              "border-b border-border/60 last:border-b-0",
              open === i && "bg-primary/[0.025]"
            )}
            role="listitem"
          >
            <button
              className="w-full bg-transparent border-none text-foreground px-6 py-5 text-[15.5px] font-medium text-left flex justify-between items-center hover:bg-foreground/[0.02] transition-colors"
              onClick={() => handleToggle(i)}
              aria-expanded={open === i}
              aria-controls={`faq-answer-${i}`}
              id={`faq-question-${i}`}
            >
              <span>{f.q}</span>
              <span className="text-[22px] text-primary font-light w-5 text-center shrink-0">
                {open === i ? "−" : "+"}
              </span>
            </button>
            {open === i && (
              <div
                className="px-6 pb-5 text-[14.5px] leading-relaxed text-muted-foreground max-w-[640px]"
                id={`faq-answer-${i}`}
                role="region"
                aria-labelledby={`faq-question-${i}`}
              >
                {f.a}
              </div>
            )}
          </div>
        ))}
      </div>

      {landing ? (
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <Link
            href="/faq"
            className="text-primary border-b border-dashed border-primary/40 hover:text-primary/80 transition-colors"
          >
            See all the FAQs →
          </Link>
        </div>
      ) : null}
    </section>
  );
};
